import React from 'react';
import styled, { css } from 'astroturf/react';

import { Accordion } from 'js/design_system/Accordion';
import Badge from 'js/design_system/Badge';
import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { Icon } from 'js/design_system/Icon';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { CHROME_EXTENSION_URL } from 'js/co_scientist_agent/ui/chromeExtension';

import ResearchExamples from './ResearchExamples';
import {
  ClosingCta,
  displayHeading,
  FaqAnswer,
  FaqList,
  LandingFooter,
  Page,
  PrimaryCta,
  SecondaryText,
  Section,
  semibold,
} from './shared';
import {
  CoScientistLandingImages,
  ImageSize,
  ResponsiveImage,
  SECTION_IDS,
} from './types';

const Hero = styled.section`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm-24, 24px);
  padding: calc(var(--spacing-lg-80, 80px) + var(--spacing-sm-32, 32px))
    var(--spacing-sm-24, 24px) var(--spacing-lg-64, 64px);
  text-align: center;

  @include breakpoint(small) {
    padding: var(--spacing-sm-32, 32px) var(--spacing-sm-24, 24px);
  }
`;

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm-24, 24px);

  > p {
    max-width: 740px;
  }
`;

const FeatureList = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg-64, 64px);
  width: 100%;
  max-width: 956px;
  text-align: start;

  @include breakpoint(small) {
    gap: var(--spacing-md-48, 48px);
  }
`;

const Feature = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 338px) minmax(0, 566px);
  grid-template-areas: 'copy media';
  justify-content: space-between;
  align-items: center;
  gap: var(--spacing-md-48, 48px);

  @media (max-width: 991px) {
    grid-template-columns: minmax(0, 566px);
    grid-template-areas: 'media' 'copy';
    justify-content: center;
    gap: var(--spacing-sm-24, 24px);
  }
`;

const FeatureCopy = styled.div`
  grid-area: copy;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
`;

const featureHeading = css`
  &[class] {
    font-weight: var(--type-font-weight-700, 700);
  }
`;

const FeatureMedia = styled.picture`
  grid-area: media;
  display: block;
  overflow: hidden;
  border-radius: var(--border-radius-radius-md, 8px);

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;

const SecurityCards = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--spacing-sm-24, 24px);
  width: 100%;
  max-width: 1304px;
  text-align: start;

  @include breakpoint(small) {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--spacing-xs-16, 16px);
  }
`;

const SecurityCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  padding: var(--spacing-sm-24, 24px);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--border-radius-radius-lg, 16px);
`;

const IconTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin-bottom: var(--spacing-xs-8, 8px);
  border-radius: var(--border-radius-radius-md, 8px);
  background: var(--background-beige-med, #f2f0ec);
`;

const SecurityCardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-4, 4px);
`;

const SecurityGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-sm-32, 32px);
  width: 100%;
`;

const examplesHeading = css`
  @media (max-width: 991px) {
    padding: 0 var(--spacing-sm-24, 24px);
  }
`;

const HOW_IT_WORKS: {
  key: keyof CoScientistLandingImages['howItWorks'];
  title: string;
  description: string;
  alt: string;
  /* Intrinsic image sizes, so space is reserved before lazy images load and #anchors land */
  desktopSize: ImageSize;
  mobileSize: ImageSize;
}[] = [
  {
    key: 'optimizedForResearch',
    title: 'Optimized for academic research',
    description:
      'General AI tools are built to do a bit of everything. Co-scientist is optimized for ' +
      'research: we test AI models on real research tasks and use the best one for each ' +
      'step. You get results you can trust and build on.',
    alt: 'A research request broken into steps, each handled by the best model for the job',
    desktopSize: [1132, 708],
    mobileSize: [1026, 1152],
  },
  {
    key: 'proactiveSuggestions',
    title: 'Proactively moves your research forward',
    description:
      'Co-scientist reads across your field’s literature and your own record, then ' +
      'proposes the next move: the paper you missed, the method worth borrowing, the ' +
      'grant that fits. Suggestions improve the more you interact.',
    alt: 'Co-scientist suggesting a method from your field and a matching grant',
    desktopSize: [1132, 708],
    mobileSize: [1026, 804],
  },
  {
    key: 'personalized',
    title: 'Personalized to you',
    description:
      'Co-scientist is a research collaborator that understands your work. It knows ' +
      'your papers and your process, working the way a close colleague would.',
    alt: 'Your methods, private datasets, published papers and grants connected to you',
    desktopSize: [1128, 708],
    mobileSize: [1026, 942],
  },
];

const SECURITY = [
  {
    icon: 'lock',
    title: 'No training on your data',
    description:
      'Models are never trained on your data. Your data personalizes your experience ' +
      'and nothing more.',
  },
  {
    icon: 'visibility_off',
    title: 'Research remains private',
    description:
      'Your papers, files, and questions stay yours. Your data is never sold nor ' +
      'visible to others.',
  },
  {
    icon: 'delete_history',
    title: 'Remove your research at anytime',
    description:
      'Disconnect Drive, revoke browser access, or delete your data anytime.',
  },
];

const buildFaqs = (
  pricingUrl: string,
): { question: string; answer: React.ReactNode[] }[] => [
  {
    question:
      'How much does Co-scientist cost? What’s included in a subscription?',
    answer: [
      'Co-scientist is free to start. Your free trial allows you access to all ' +
        'Co-scientist features to give you room to try it on your own research, start ' +
        'chats, build tools, and receive proactive suggestions. After the free trial ' +
        'ends, viewing existing chats, tools and files remains free. To continue ' +
        'chatting and working after your free trial, however, upgrade to a monthly plan.',
      <>
        A monthly plan allows unlimited usage to start chats, build tools,
        analyze data, and receive unlimited suggestions.{' '}
        <BodyLink href={pricingUrl} size="md" underline>
          View Pricing
        </BodyLink>{' '}
        to learn more.
      </>,
    ],
  },
  {
    question: 'Who owns the output that Co-scientist generates?',
    answer: [
      'You own what Co-scientist generates. The papers, data, and files you upload ' +
        'stay yours, and the analyses, code, figures, and drafts Co-scientist creates ' +
        'are yours. Use them in your papers, grant applications, and teaching like any ' +
        'of your own work.',
    ],
  },
  {
    question: 'What is the Academia Co-scientist Chrome extension?',
    answer: [
      'The Chrome extension is an optional browser extension that lets Co-scientist ' +
        'see the research pages you read, such as papers, preprints, and journal ' +
        'articles, so it can connect them to your work and suggest next steps sooner. ' +
        'Non-research browsing is excluded.',
      <>
        The extension is optional. To install the Chrome Extension, visit the{' '}
        <BodyLink href={CHROME_EXTENSION_URL} size="md" underline>
          Chrome Store
        </BodyLink>
        . Revoke access at any time.
      </>,
    ],
  },
  {
    question: 'Can I share Co-scientist with my colleagues?',
    answer: [
      'Yes. To invite your colleagues: Sign into ' +
        'Co-scientist; navigate to the lower left corner in the menu and click ' +
        '‘Invite colleagues’; and share the provided link with anyone you wish. ' +
        'Sharing at this time allows others to use Co-scientist, but they won’t be ' +
        'able to see your work. Stay tuned for the ability to share your work and ' +
        'collaborate with others in Co-scientist.',
    ],
  },
  {
    question: 'Can I access the code and scripts directly?',
    answer: [
      'Yes. The analyses and tools Co-scientist builds are directly accessible for you.',
    ],
  },
  {
    question: 'How do I get started?',
    answer: [
      'Start a trial and sign in with a free Academia.edu account. From there, briefly ' +
        'tell Co-scientist about yourself and it can begin working on tasks or ' +
        'suggesting ways to advance your research.',
    ],
  },
];

const ResponsivePicture: React.FC<{
  image: ResponsiveImage;
  alt: string;
  desktopSize: ImageSize;
  mobileSize: ImageSize;
}> = ({ image, alt, desktopSize, mobileSize }) => (
  <FeatureMedia>
    <source
      media="(max-width: 567px)"
      srcSet={image.mobile}
      width={mobileSize[0]}
      height={mobileSize[1]}
    />
    <img
      src={image.desktop}
      alt={alt}
      width={desktopSize[0]}
      height={desktopSize[1]}
      loading="lazy"
    />
  </FeatureMedia>
);

type CoScientistLandingPageProps = {
  heroCtaUrl: string;
  closingCtaUrl: string;
  hasAccess: boolean;
  privacyPolicyUrl: string;
  pricingUrl: string;
  academiaUrl: string;
  images: CoScientistLandingImages;
};

const CoScientistLandingPage: React.FC<CoScientistLandingPageProps> = ({
  heroCtaUrl,
  closingCtaUrl,
  hasAccess,
  privacyPolicyUrl,
  pricingUrl,
  academiaUrl,
  images,
}) => {
  return (
    <Page>
      <Hero>
        <HeroCopy>
          <Badge
            text="Introducing Academia Co-scientist"
            color="var(--brand-colors-expanded-colors-lime-900, #333e2c)"
            backgroundColor="var(--brand-colors-expanded-colors-lime-100, #f2f5ef)"
            style={{
              border:
                '1px solid var(--brand-colors-expanded-colors-lime-200, #dee6d6)',
            }}
          />
          <Heading
            as="h1"
            fontFamily="serif"
            size="lg"
            className={displayHeading}
          >
            Your proactive research collaborator
          </Heading>
          <SecondaryText size="lg">
            Academia Co-scientist is the AI agent built for your research. It
            works with your data to run analyses, build models, and flag what
            you might have missed.
          </SecondaryText>
        </HeroCopy>
        <PrimaryCta ctaUrl={heroCtaUrl} hasAccess={hasAccess} />
      </Hero>

      <Section flush>
        <Heading
          as="h2"
          fontFamily="sans-serif"
          size="sm"
          className={examplesHeading}
        >
          How researchers are using Co‑scientist
        </Heading>
        <ResearchExamples images={images.examples} />
      </Section>

      <Section id={SECTION_IDS.howItWorks}>
        <Heading
          as="h2"
          fontFamily="serif"
          size="lg"
          className={displayHeading}
        >
          How Co-scientist works
        </Heading>
        <FeatureList>
          {HOW_IT_WORKS.map((feature) => (
            <Feature key={feature.key}>
              <ResponsivePicture
                image={images.howItWorks[feature.key]}
                alt={feature.alt}
                desktopSize={feature.desktopSize}
                mobileSize={feature.mobileSize}
              />
              <FeatureCopy>
                <Heading
                  as="h3"
                  fontFamily="sans-serif"
                  size="sm"
                  className={featureHeading}
                >
                  {feature.title}
                </Heading>
                <Body size="md">{feature.description}</Body>
              </FeatureCopy>
            </Feature>
          ))}
        </FeatureList>
      </Section>

      <Section id={SECTION_IDS.dataPrivacy}>
        <Heading
          as="h2"
          fontFamily="serif"
          size="lg"
          className={displayHeading}
        >
          Your data stays secure
        </Heading>
        <SecurityGroup>
          <SecurityCards>
            {SECURITY.map((item) => (
              <SecurityCard key={item.title}>
                <IconTile>
                  <Icon name={item.icon} aria-hidden />
                </IconTile>
                <SecurityCardText>
                  <Body as="h3" size="lg" className={semibold}>
                    {item.title}
                  </Body>
                  <SecondaryText size="md">{item.description}</SecondaryText>
                </SecurityCardText>
              </SecurityCard>
            ))}
          </SecurityCards>
          <SecondaryText size="sm">
            For more details on storage, retention, and deletion, see our{' '}
            <BodyLink href={privacyPolicyUrl} size="sm" underline>
              privacy policy
            </BodyLink>
            .
          </SecondaryText>
        </SecurityGroup>
      </Section>

      <Section id={SECTION_IDS.faqs}>
        <Heading
          as="h2"
          fontFamily="serif"
          size="lg"
          className={displayHeading}
        >
          Frequently asked questions
        </Heading>
        <FaqList>
          <Accordion
            sections={buildFaqs(pricingUrl).map((faq) => ({
              title: (
                <Body as="h3" size="lg" className={semibold}>
                  {faq.question}
                </Body>
              ),
              contents: faq.answer.map((paragraph, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <FaqAnswer key={i} size="md">
                  {paragraph}
                </FaqAnswer>
              )),
            }))}
          />
        </FaqList>
      </Section>

      <ClosingCta
        heading="Put Co-scientist to work on your research"
        ctaUrl={closingCtaUrl}
        hasAccess={hasAccess}
      />

      <LandingFooter
        academiaUrl={academiaUrl}
        academiaLogoWhite={images.academiaLogoWhite}
      />
    </Page>
  );
};

export default CoScientistLandingPage;
